import React, { useState, useEffect } from 'react';
import { scoreToGrade, pointsToNextGrade } from '../utils/GradeUtils';
import * as api from '../services/api';

interface RemediationItem {
  title: string;
  detail: string;
  impact: number;
  effort: string;
}

interface CriticalPathCardProps {
  clientName: string;
  clientId?: string;
  currentScore: number;
  items?: RemediationItem[];
  onVerify?: () => void;
}

// No generated mock data — remediation items come from API or are empty

const CriticalPathCard: React.FC<CriticalPathCardProps> = ({ clientName, clientId, currentScore, items, onVerify }) => {
  const [apiItems, setApiItems] = useState<RemediationItem[] | null>(null);

  useEffect(() => {
    if (clientId) {
      api.getRemediationItems(clientId).then((data: any) => {
        if (data?.items?.length) {
          setApiItems(data.items.map((i: any) => ({ title: i.title, detail: i.detail, impact: i.impact, effort: i.effort || '~2 hours' })));
        }
      }).catch(() => { /* fall back to generated items */ });
    }
  }, [clientId]);

  const { grade, color } = scoreToGrade(currentScore);
  const next = pointsToNextGrade(currentScore);
  const remediations = items || apiItems || [];
  const totalImpact = remediations.reduce((s, r) => s + r.impact, 0);

  if (!next) {
    return (
      <div className="bg-white/[0.02] border border-anchor-teal/20 rounded-xl p-5">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-2 h-2 rounded-full bg-anchor-teal" />
          <span className="text-sm font-medium text-anchor-teal">Anchor Set</span>
        </div>
        <p className="text-xs text-white/40">{clientName} is at {grade} ({currentScore}%) — no critical remediation needed.</p>
      </div>
    );
  }

  const targetScore = Math.min(100, currentScore + totalImpact);
  const targetGrade = scoreToGrade(targetScore);

  return (
    <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-medium text-white">Critical Path to {next.nextGrade}</h3>
          <p className="text-xs text-white/40 mt-0.5">{clientName}</p>
        </div>
        <div className="text-right">
          <span className="text-xs text-white/40">Current: </span>
          <span className="text-sm font-medium" style={{ color }}>{grade}</span>
          <span className="text-xs text-white/40"> → Target: </span>
          <span className="text-sm font-medium" style={{ color: targetGrade.color }}>{targetGrade.grade}</span>
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-2 bg-white/[0.04] rounded-full overflow-hidden mb-4">
        <div className="h-full rounded-full relative" style={{ width: `${targetScore}%` }}>
          <div className="absolute inset-0 rounded-full" style={{ width: `${(currentScore / targetScore) * 100}%`, background: `${color}` }} />
          <div className="absolute inset-0 rounded-full opacity-30" style={{ background: targetGrade.color }} />
        </div>
      </div>

      {/* Remediation items */}
      <div className="mb-3">
        {remediations.length === 0 ? (
          <p className="text-xs text-white/30 py-4 text-center">Run an agent audit to generate remediation recommendations.</p>
        ) : (
        <p className="text-xs text-white/50 mb-3">
          Fix {remediations.length} item{remediations.length !== 1 ? 's' : ''} for +{totalImpact.toFixed(1)}% improvement:
        </p>
        )}
        {remediations.length > 0 && (
          <div className="space-y-2.5">
            {remediations.map((item, i) => (
              <div key={i} className="flex items-start gap-3 py-2 px-3 bg-white/[0.01] border border-white/[0.03] rounded-lg">
                <span className="text-xs font-medium text-anchor-blue mt-0.5">{i + 1}.</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white/80">{item.title}</div>
                  <div className="text-xs text-white/40 mt-0.5">{item.detail}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-xs font-medium text-anchor-teal">+{item.impact.toFixed(1)}%</div>
                  <div className="text-[10px] text-white/30">{item.effort}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Verify Remediation button */}
      <button
        onClick={() => onVerify?.()}
        disabled={!onVerify}
        className="w-full py-2.5 px-4 bg-anchor-blue/10 border border-anchor-blue/30 rounded-lg text-sm text-anchor-blue hover:bg-anchor-blue/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
      >
        Verify Remediation
      </button>
    </div>
  );
};

export default CriticalPathCard;
